import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Spinner, Alert, Table, Badge, Button } from 'react-bootstrap';
import { Calendar, Download, Filter } from 'lucide-react';
import { getAllEmployeeBalances } from '../../components/api/LeaveApi';

const LeaveBalance = () => {
     const [balances, setBalances] = useState([]);
     const [allBalances, setAllBalances] = useState([]);
     const [filteredBalances, setFilteredBalances] = useState([]);
     const [loading, setLoading] = useState(true);
     const [error, setError] = useState('');
     const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
     const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

     // Filters for table view
     const [filterEmpCode, setFilterEmpCode] = useState('');
     const [filterName, setFilterName] = useState('');
     const [filterLeaveType, setFilterLeaveType] = useState('');

     // Get user role
     const userRole = sessionStorage.getItem('user_role');
     const loggedInEmpCode = sessionStorage.getItem('user_code');
     const isHROrAdmin = ['HR', 'SUPER_ADMIN'].includes(userRole);

     useEffect(() => {
          const handleResize = () => setIsMobile(window.innerWidth < 768);
          window.addEventListener('resize', handleResize);
          return () => window.removeEventListener('resize', handleResize);
     }, []);

     useEffect(() => {
          fetchBalances();
     }, [selectedYear]);

     useEffect(() => {
          applyFilters();
     }, [filterEmpCode, filterName, filterLeaveType, allBalances]);

     const fetchBalances = async () => {
          try {
               setLoading(true);
               setError('');
               const year = new Date().getFullYear();
               const response = await getAllEmployeeBalances(year);
               console.log('API Response:', response);
               console.log('User Role:', userRole);
               console.log('Is HR/Admin:', isHROrAdmin);

               const data = Array.isArray(response) ? response : response.data || [];

               if (isHROrAdmin) {
                    console.log('Showing table view for HR/Admin/SUPER_ADMIN');
                    setAllBalances(data);
                    setFilteredBalances(data);
               } else {
                    console.log('Showing cards for employee:', loggedInEmpCode);
                    const empData = data.filter(b => b.emp_code === loggedInEmpCode);
                    setBalances(empData);

                    if (empData.length === 0) {
                         setError('No leave balances found for your account. Please contact HR.');
                    }
               }
          } catch (error) {
               console.error('Error fetching balances:', error);
               setError('Failed to fetch leave balances');
               setBalances([]);
               setAllBalances([]);
          } finally {
               setLoading(false);
          }
     };

     const applyFilters = () => {
          let filtered = [...allBalances];

          if (filterEmpCode) {
               filtered = filtered.filter(b =>
                    b.emp_code.toLowerCase().includes(filterEmpCode.toLowerCase())
               );
          }

          if (filterName) {
               filtered = filtered.filter(b =>
                    b.employee_name?.toLowerCase().includes(filterName.toLowerCase())
               );
          }

          if (filterLeaveType) {
               filtered = filtered.filter(b => b.leave_type === filterLeaveType);
          }

          setFilteredBalances(filtered);
     };

     const clearFilters = () => {
          setFilterEmpCode('');
          setFilterName('');
          setFilterLeaveType('');
     };

     const exportToCSV = () => {
          const csvContent = 'Employee Code,Employee Name,Leave Type,Total Allocated,Used,Remaining,Year Start,Year End\n' +
               filteredBalances.map(b =>
                    `${b.emp_code},"${b.employee_name || 'N/A'}",${b.leave_type},${b.total_leaves},${b.used_leaves},${b.remaining_leaves},${b.year_start},${b.year_end}`
               ).join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `leave_balances_${selectedYear}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
     };

     const getLeaveTypeColor = (type) => {
          const colors = {
               'PL': 'primary',
               'CL': 'success',
               'SL': 'warning'
          };
          return colors[type] || 'secondary';
     };

     const getLeaveTypeName = (type) => {
          const names = {
               'PL': 'Privilege Leave',
               'CL': 'Casual Leave',
               'SL': 'Sick Leave'
          };
          return names[type] || type;
     };

     if (loading) {
          return (
               <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Loading balances...</p>
               </div>
          );
     }

     return (
          <div className="leave-balance-section" style={{ padding: isMobile ? '12px' : '20px' }}>
               <Row className="mb-3">
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                         <Form.Select
                              style={{ width: isMobile ? '100%' : '200px' }}
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                              size="sm"
                         >
                              {[...Array(5)].map((_, i) => {
                                   const year = new Date().getFullYear() - 2 + i;
                                   return <option key={year} value={year}>{year}-{year + 1}</option>;
                              })}
                         </Form.Select>
                    </Col>
               </Row>

               {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

               {/* Employee View: Cards */}
               {!isHROrAdmin && (
                    <>
                         <Row>
                              {balances.map((balance, index) => (
                                   <Col lg={4} md={6} xs={12} key={index} className="mb-3">
                                        <Card className={`leave-balance-card border-${getLeaveTypeColor(balance.leave_type)}`}>
                                             <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
                                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                                       <div>
                                                            <h6 className="text-muted mb-1" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                                                                 {getLeaveTypeName(balance.leave_type)}
                                                            </h6>
                                                            <h2 className={`mb-0 text-${getLeaveTypeColor(balance.leave_type)}`} style={{ fontSize: isMobile ? '24px' : '32px' }}>
                                                                 {parseFloat(balance.remaining_leaves).toFixed(1)}
                                                            </h2>
                                                       </div>
                                                       <Calendar size={isMobile ? 32 : 40} className={`text-${getLeaveTypeColor(balance.leave_type)} opacity-25`} />
                                                  </div>

                                                  <div className="leave-stats">
                                                       <div className="d-flex justify-content-between mb-2">
                                                            <small className="text-muted" style={{ fontSize: isMobile ? '11px' : '12px' }}>Total Allocated</small>
                                                            <strong style={{ fontSize: isMobile ? '13px' : '14px' }}>{parseFloat(balance.total_leaves).toFixed(1)}</strong>
                                                       </div>
                                                       <div className="d-flex justify-content-between mb-2">
                                                            <small className="text-muted" style={{ fontSize: isMobile ? '11px' : '12px' }}>Used</small>
                                                            <strong className="text-danger" style={{ fontSize: isMobile ? '13px' : '14px' }}>{parseFloat(balance.used_leaves).toFixed(1)}</strong>
                                                       </div>
                                                       <div className="progress" style={{ height: '8px' }}>
                                                            <div
                                                                 className={`progress-bar bg-${getLeaveTypeColor(balance.leave_type)}`}
                                                                 style={{ width: `${(balance.remaining_leaves / balance.total_leaves) * 100}%` }}
                                                            ></div>
                                                       </div>
                                                       <small className="text-muted mt-2 d-block" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                            {Math.round((balance.remaining_leaves / balance.total_leaves) * 100)}% remaining
                                                       </small>
                                                  </div>

                                                  <div className="text-muted mt-3" style={{ fontSize: isMobile ? '11px' : '12px' }}>
                                                       <Calendar size={14} className="me-1" />
                                                       {new Date(balance.year_start).toLocaleDateString()} - {new Date(balance.year_end).toLocaleDateString()}
                                                  </div>
                                             </Card.Body>
                                        </Card>
                                   </Col>
                              ))}
                         </Row>

                         {balances.length === 0 && (
                              <Card className="text-center py-5">
                                   <Card.Body>
                                        <Calendar size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                        <h5 className="text-muted" style={{ fontSize: isMobile ? '16px' : '20px' }}>No leave balances found</h5>
                                        <p className="text-muted" style={{ fontSize: isMobile ? '13px' : '14px' }}>Contact HR to credit leaves for this period</p>
                                   </Card.Body>
                              </Card>
                         )}
                    </>
               )}

               {/* HR/Admin/SUPER_ADMIN View: Table with Filters */}
               {isHROrAdmin && (
                    <>
                         {/* Filters */}
                         <Row className="mb-3">
                              <Col xs={12}>
                                   <Card>
                                        <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
                                             <Row className="g-2">
                                                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold" style={{ fontSize: isMobile ? '12px' : '13px' }}>
                                                                 <Filter size={12} className="me-1" />
                                                                 Employee Code
                                                            </Form.Label>
                                                            <Form.Control
                                                                 type="text"
                                                                 placeholder="Search by code..."
                                                                 value={filterEmpCode}
                                                                 onChange={(e) => setFilterEmpCode(e.target.value)}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            />
                                                       </Form.Group>
                                                  </Col>
                                                  <Col xs={12} md={4} className="mb-2 mb-md-0">
                                                       <Form.Group>
                                                            <Form.Label className="small fw-semibold" style={{ fontSize: isMobile ? '12px' : '13px' }}>Employee Name</Form.Label>
                                                            <Form.Control
                                                                 type="text"
                                                                 placeholder="Search by name..."
                                                                 value={filterName}
                                                                 onChange={(e) => setFilterName(e.target.value)}
                                                                 size="sm"
                                                                 style={{ fontSize: isMobile ? '13px' : '14px' }}
                                                            />
                                                       </Form.Group>
                                                  </Col>


                                                  <Col xs={12} md={4} className="d-flex align-items-end gap-2">
                                                       <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={clearFilters}
                                                            style={{
                                                                 fontSize: '12px',
                                                                 padding: '6px 16px',
                                                                 flex: isMobile ? '1' : '0 0 auto',
                                                                 minWidth: isMobile ? '0' : '80px'
                                                            }}
                                                       >
                                                            Clear
                                                       </Button>
                                                       <Button
                                                            variant="primary"
                                                            size="sm"
                                                            onClick={exportToCSV}
                                                            disabled={filteredBalances.length === 0}
                                                            style={{
                                                                 fontSize: '12px',
                                                                 padding: '6px 16px',
                                                                 flex: isMobile ? '1' : '0 0 auto',
                                                                 minWidth: isMobile ? '0' : '100px'
                                                            }}
                                                       >
                                                            <Download size={14} className="me-1" />
                                                            Export
                                                       </Button>
                                                  </Col>
                                             </Row>
                                        </Card.Body>
                                   </Card>
                              </Col>
                         </Row>

                         {/* Table - Desktop / Cards - Mobile */}
                         <Card>
                              <Card.Header className="bg-light" style={{ padding: isMobile ? '10px 12px' : '12px 16px' }}>
                                   <h5 className="mb-0" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                        All Employee Leave Balances ({filteredBalances.length} records)
                                   </h5>
                              </Card.Header>
                              <Card.Body className="p-0">
                                   {isMobile ? (
                                        // Mobile Card View
                                        <div style={{ padding: '12px' }}>
                                             {filteredBalances.map((balance, index) => (
                                                  <Card key={index} className="mb-2" style={{ fontSize: '13px' }}>
                                                       <Card.Body style={{ padding: '12px' }}>
                                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                                 <div>
                                                                      <strong style={{ fontSize: '14px' }}>{balance.emp_code}</strong>
                                                                      <div className="text-muted small">{balance.employee_name || 'N/A'}</div>
                                                                 </div>
                                                            </div>
                                                            <Row className="g-2">
                                                                 <Col xs={4}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Total</small>
                                                                      <strong style={{ fontSize: '14px' }}>{parseFloat(balance.total_leaves).toFixed(1)}</strong>
                                                                 </Col>
                                                                 <Col xs={4}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Used</small>
                                                                      <strong className="text-danger" style={{ fontSize: '14px' }}>{parseFloat(balance.used_leaves).toFixed(1)}</strong>
                                                                 </Col>
                                                                 <Col xs={4}>
                                                                      <small className="text-muted d-block" style={{ fontSize: '11px' }}>Balance</small>
                                                                      <strong className="text-success" style={{ fontSize: '14px' }}>{parseFloat(balance.remaining_leaves).toFixed(1)}</strong>
                                                                 </Col>
                                                            </Row>
                                                            <div className="mt-2">
                                                                 <div className="progress" style={{ height: '6px' }}>
                                                                      <div
                                                                           className={`progress-bar bg-${getLeaveTypeColor(balance.leave_type)}`}
                                                                           style={{ width: `${(balance.remaining_leaves / balance.total_leaves) * 100}%` }}
                                                                      ></div>
                                                                 </div>
                                                                 <small className="text-muted" style={{ fontSize: '11px' }}>
                                                                      {Math.round((balance.remaining_leaves / balance.total_leaves) * 100)}% remaining
                                                                 </small>
                                                            </div>
                                                       </Card.Body>
                                                  </Card>
                                             ))}
                                        </div>
                                   ) : (
                                        // Desktop Table View
                                        <div className="table-responsive">
                                             <Table hover className="mb-0" style={{ fontSize: '14px' }}>
                                                  <thead className="table-light">
                                                       <tr>
                                                            <th>Emp Code</th>
                                                            <th>Employee Name</th>
                                                            <th className="text-end">Total Credit</th>
                                                            <th className="text-end">Used</th>
                                                            <th className="text-end">Balance</th>
                                                            <th className="text-center">Progress</th>
                                                       </tr>
                                                  </thead>
                                                  <tbody>
                                                       {filteredBalances.map((balance, index) => (
                                                            <tr key={index}>
                                                                 <td><strong>{balance.emp_code}</strong></td>
                                                                 <td>{balance.employee_name || 'N/A'}</td>
                                                                 <td className="text-end">
                                                                      <strong>{parseFloat(balance.total_leaves).toFixed(1)}</strong>
                                                                 </td>
                                                                 <td className="text-end text-danger">
                                                                      {parseFloat(balance.used_leaves).toFixed(1)}
                                                                 </td>
                                                                 <td className="text-end text-success">
                                                                      <strong>{parseFloat(balance.remaining_leaves).toFixed(1)}</strong>
                                                                 </td>
                                                                 <td>
                                                                      <div className="d-flex align-items-center justify-content-center">
                                                                           <div className="progress flex-grow-1 me-2" style={{ height: '6px', maxWidth: '150px' }}>
                                                                                <div
                                                                                     className={`progress-bar bg-${getLeaveTypeColor(balance.leave_type)}`}
                                                                                     style={{ width: `${(balance.remaining_leaves / balance.total_leaves) * 100}%` }}
                                                                                ></div>
                                                                           </div>
                                                                           <small className="text-muted" style={{ minWidth: '40px' }}>
                                                                                {Math.round((balance.remaining_leaves / balance.total_leaves) * 100)}%
                                                                           </small>
                                                                      </div>
                                                                 </td>
                                                            </tr>
                                                       ))}
                                                  </tbody>
                                             </Table>
                                        </div>
                                   )}

                                   {filteredBalances.length === 0 && (
                                        <div className="text-center py-5">
                                             <Calendar size={isMobile ? 48 : 64} className="text-muted mb-3" />
                                             <h5 className="text-muted" style={{ fontSize: isMobile ? '16px' : '18px' }}>No records found</h5>
                                             <p className="text-muted" style={{ fontSize: isMobile ? '13px' : '14px' }}>Try adjusting your filters</p>
                                        </div>
                                   )}
                              </Card.Body>
                         </Card>
                    </>
               )}
          </div>
     );
};

export default LeaveBalance;